/**
 * Export Service - PDF & CMMS Export Functionality
 * Handles structured data export for work completion reports
 */

import { jsPDF } from 'jspdf';
import { auditService } from './auditService';

// RaconWorks logo as base64 (placeholder - replace with actual logo)
const LOGO_BASE64 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAAAyCAYAAACqNX6+AAAABHNCSVQICAgIfAhkiAAAAAlwSFlzAAAOxAAADsQBlSsOGwAAABl0RVh0U29mdHdhcmUAd3d3Lmlua3NjYXBlLm9yZ5vuPBoAAANhSURBVHic7ZtNaBNBFMd/s5tNmtSmNlZrq1gFwYMeBBG8ePDgQfDgwYsHD4IXLx48ePAgePHgQfDgwYMHD4IXDx4EPXjw4MGD4MWDB0EPHgQ9eBAURGzT2CbZjIekaZpkN9nd2U3T/cFAN7Mz7/3nzZs3s7sCwzDMwFAoFGq1WoX7+/tJnU4X1ev1Hw4ODj4fH58YI4K9gMPhOLu+vn4vFotdBHC73c8XFxfvxmKxXUBXXV3d08nJyYuZTOYgYLXb7Q8mJiZOq9Xq5mKxeFSpVDq2trb2dnZ2dldXV3d3dXV1RCKR3UAqlcoVCoXjkUjkzPr6+r1EInFRpVLhcDieBYPBu+l0ehfQ5XK5niwsLNzNZDK7AKvNZnswPj5+WqPRNBeLxaOVSqVja2trT3d3d3dVVVV3ZWXl7mAwuDsajZ7Z2Nj4PZlMXlSpVOBwOJ75/f672Wx2N9DpcDieTk5OntZqtU2lUulIpVLp3N7e3tvT09NdWVm5u6Ki4vdQKLQ7Ho+f2dzc/D2VSl1UKpU4nc5ng4ODd3O53C6gy+FwPJ2amjqt1WobSqXS0XK53Lm1tbW3t7e3u6Kionvz8/O7g8Hg7ng8fmZ9ff33dDp9Ua1Wg9PpfDY0NHQnn8/vArqcTufTmZmZ0zqdTl8qlQ6Xy+XOra2t/X19fT1VVVXdFRUVvweDwd3xePx0LBb7PZ1OX1Kr1eBwOJ4NDw/fyefzu4Cup0+f1hsMhqZSqdT5/Pnz/r6+vtrq6ureysrK3cFgsC4ej5+Ox+P/ZjKZi0qlEqfT+XxoaOhOPp/fBXRardZng4ODd/R6vaFYLB4ul8udW1tb+/r7++uqq6t7Kyoqdnu93rpoNHomHo+fyWQyFxUKBS6Xy/Py8vKdXC63E+gqKSlZodfrm0ulUufm5ua+gYGButra2urKykrfr1+/6qLR6Ol4PH4mk8lcUCgUuFyuFwsLC3dyudwuoMtisayqra01lEqlzq2trX0DAwN1dXV1NVVVVT5JkupisdjpeDx+JpPJXFAoFLjd7hfz8/N38vn8TqCrqKho1dGjR5uKxWLn5ubmvoGBgbr6+vqaqqoq348fP+pisdjpRCLxeyaTuaBQKHC73S/n5ubuFAqFnUBXQUHBqkOHDjWVSqXOjY2NfUNDQw2NjY01lZWVvu/fv9fF4/HTiUTi90wmQ0NDQ01tbW2N3+93VlZWGsvl8r8bGxv7hoaGGhobG2sqKyv7vn79WhcMBn9PJpP/ZjKZfwG3bNmyZcuWLVu2bNmyZcuWLVu2bNmyZet/bH8B8sQ2hX8k0LAAAAAASUVORK5CYII=';

// Condition score mapping
const CONDITION_LABELS = {
    1: { label: 'Excellent', color: '#22c55e' },
    2: { label: 'Goed', color: '#84cc16' },
    3: { label: 'Redelijk', color: '#eab308' },
    4: { label: 'Matig', color: '#f97316' },
    5: { label: 'Slecht', color: '#ef4444' },
    6: { label: 'Sloop', color: '#991b1b' }
};

/**
 * Build a structured export payload from permit and completion data
 */
export const buildExportPayload = (permit, checklist, completionData) => {
    const now = new Date();

    return {
        // Header / Identification
        exportVersion: '1.0',
        exportedAt: now.toISOString(),

        // Work Plan Identification
        werkplanNr: permit.ultimoNr || permit.id || 'CONCEPT',
        werkplanId: permit.id,

        // Location Hierarchy
        locatie: permit.location || '',
        hal: permit.hall || '',
        lijn: permit.line || '',
        sublijn: permit.subline || '',

        // Task Details
        component: permit.component || '',
        taak: permit.task || '',
        omschrijving: permit.description || '',

        // Personnel
        uitvoerder: permit.executor || '',
        uitvoerderId: permit.assignedTo || '',
        aanvrager: permit.applicant || '',

        // Timestamps
        aangemaakt: permit.createdAt || '',
        gestart: permit.startedAt || '',
        afgerond: now.toISOString(),

        // LMRA Status
        lmraVoltooid: checklist?.lmraLocked === false,

        // Condition Assessment (NEN 2767)
        conditie: {
            score: completionData?.conditionScore || null,
            label: CONDITION_LABELS[completionData?.conditionScore]?.label || 'Niet ingevuld',
            color: CONDITION_LABELS[completionData?.conditionScore]?.color || '#6b7280'
        },

        // Work Instruction Feedback
        werkinstructie: {
            klopte: completionData?.instructionAnswer === 'yes',
            antwoord: completionData?.instructionAnswer || null,
            feedback: completionData?.instructionFeedback || ''
        },

        // Mechanic's Report (Voice/Text Feedback)
        monteursVerslag: completionData?.feedbackText || '',

        // ISO 14224 Failure Data (if applicable)
        storing: completionData?.isFailure ? {
            isStoringGemeld: true,
            component: completionData?.isoComponent || '',
            failureMode: completionData?.isoMode || '',
            oorzaak: completionData?.isoCause || '',
            hadFoutcode: completionData?.hasFaultCode === 'yes',
            foutcodeFotos: completionData?.faultCodePhotos || []
        } : null,

        // Photos
        fotos: {
            loto: permit.lotoPhotos || [],
            taak: permit.taskImageUrls || (permit.taskImageUrl ? [permit.taskImageUrl] : []),
            storing: completionData?.faultCodePhotos || []
        },

        // Administrative Checks
        administratie: {
            ultimoIngevuld: completionData?.ultimoIngevuld || false,
            werkplekSchoon: completionData?.werkplekSchoon || false,
            onderdelenAfgeboekt: completionData?.onderdelenAfgeboekt || false
        },

        // Tech Doc Discrepancies
        techDocAfwijkingen: {
            // Photo-based discrepancies from wizard
            fotoAfwijkingen: [
                // LOTO metadata discrepancies
                ...Object.entries(permit.lotoMetadata || {})
                    .filter(([_, meta]) => meta.hasDiscrepancy || meta.discrepancyNote)
                    .map(([url, meta]) => ({
                        type: 'loto',
                        photoUrl: url,
                        tagName: meta.tagName,
                        note: meta.discrepancyNote || 'Afwijking geconstateerd'
                    })),
                // Asset metadata discrepancies
                ...Object.entries(permit.assetMetadata || {})
                    .filter(([_, meta]) => meta.hasDiscrepancy || meta.discrepancyNote)
                    .map(([url, meta]) => ({
                        type: 'asset',
                        photoUrl: url,
                        note: meta.discrepancyNote || 'Afwijking geconstateerd'
                    }))
            ],
            // General issues from completion
            tagLabel: completionData?.hasTagLabelIssue ? {
                gemeld: true,
                details: completionData?.tagLabelDetails || ''
            } : null,
            tekenpakket: completionData?.hasTekenpakketIssue ? {
                gemeld: true,
                details: completionData?.tekenpakketDetails || ''
            } : null
        }
    };
};

/**
 * Generate PDF from export payload
 */
export const generatePDF = async (payload) => {
    const doc = new jsPDF();
    let y = 20;
    const leftMargin = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - 40;

    // Helper: Add new page if needed
    const checkPageBreak = (requiredSpace = 30) => {
        if (y + requiredSpace > doc.internal.pageSize.getHeight() - 20) {
            doc.addPage();
            y = 20;
        }
    };

    // Helper: Add section header
    const addSectionHeader = (title) => {
        checkPageBreak(20);
        doc.setFillColor(30, 41, 59); // slate-800
        doc.rect(leftMargin, y, contentWidth, 8, 'F');
        doc.setFontSize(10);
        doc.setTextColor(255, 255, 255);
        doc.text(title.toUpperCase(), leftMargin + 3, y + 5.5);
        doc.setTextColor(0, 0, 0);
        y += 12;
    };

    // Helper: Add label-value pair
    const addField = (label, value, bold = false) => {
        checkPageBreak(8);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 100, 100);
        doc.text(label + ':', leftMargin, y);
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', bold ? 'bold' : 'normal');
        doc.text(String(value || '-'), leftMargin + 45, y);
        y += 6;
    };

    // === HEADER ===
    try {
        doc.addImage(LOGO_BASE64, 'PNG', leftMargin, y, 30, 12);
    } catch (e) {
        console.warn('Logo could not be added:', e);
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('WERKRAPPORT', pageWidth - 20, y + 8, { align: 'right' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(payload.werkplanNr, pageWidth - 20, y + 14, { align: 'right' });

    y += 25;

    // === ALGEMENE GEGEVENS ===
    addSectionHeader('Algemene Gegevens');
    addField('Werkplan Nr', payload.werkplanNr, true);
    addField('Locatie', payload.locatie);
    addField('Hal / Lijn', `${payload.hal} › ${payload.lijn}`);
    addField('Component', payload.component);
    addField('Taak', payload.taak);
    addField('Uitvoerder', payload.uitvoerder);
    addField('Afgerond op', new Date(payload.afgerond).toLocaleString('nl-NL'));
    y += 5;

    // === CONDITIEBEOORDELING ===
    addSectionHeader('Conditiebeoordeling (NEN 2767)');
    checkPageBreak(15);

    // Condition badge
    const condColor = payload.conditie.color;
    doc.setFillColor(parseInt(condColor.slice(1, 3), 16), parseInt(condColor.slice(3, 5), 16), parseInt(condColor.slice(5, 7), 16));
    doc.roundedRect(leftMargin, y, 50, 10, 2, 2, 'F');
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255);
    doc.text(`${payload.conditie.score || '?'} - ${payload.conditie.label}`, leftMargin + 25, y + 6.5, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    y += 18;

    // === WERKINSTRUCTIE ===
    addSectionHeader('Werkinstructie Feedback');
    addField('Klopte de instructie?', payload.werkinstructie.klopte ? 'JA ✓' : 'NEE ✗', true);
    if (!payload.werkinstructie.klopte && payload.werkinstructie.feedback) {
        checkPageBreak(20);
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('Wat klopte niet:', leftMargin, y);
        y += 5;
        doc.setTextColor(0, 0, 0);
        const feedbackLines = doc.splitTextToSize(payload.werkinstructie.feedback, contentWidth - 5);
        doc.text(feedbackLines, leftMargin + 5, y);
        y += feedbackLines.length * 4 + 5;
    }
    y += 3;

    // === MONTEURS VERSLAG ===
    addSectionHeader('Uitgevoerde Werkzaamheden');
    checkPageBreak(30);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    if (payload.monteursVerslag) {
        const verslagLines = doc.splitTextToSize(payload.monteursVerslag, contentWidth - 5);
        verslagLines.forEach(line => {
            checkPageBreak(6);
            doc.text(line, leftMargin + 3, y);
            y += 5;
        });
    } else {
        doc.setTextColor(150, 150, 150);
        doc.text('Geen toelichting opgegeven.', leftMargin + 3, y);
        doc.setTextColor(0, 0, 0);
        y += 5;
    }
    y += 5;

    // === STORING (if applicable) ===
    if (payload.storing) {
        addSectionHeader('Storingsregistratie (ISO 14224)');
        addField('Falend Component', payload.storing.component);
        addField('Failure Mode', payload.storing.failureMode);
        addField('Oorzaak', payload.storing.oorzaak);
        addField('Storingscode gefotografeerd', payload.storing.hadFoutcode ? 'JA' : 'NEE');
        y += 5;
    }

    // === TECH DOC AFWIJKINGEN ===
    const hasDiscrepancies =
        (payload.techDocAfwijkingen?.fotoAfwijkingen?.length > 0) ||
        payload.techDocAfwijkingen?.tagLabel?.gemeld ||
        payload.techDocAfwijkingen?.tekenpakket?.gemeld;

    if (hasDiscrepancies) {
        addSectionHeader('Tech Doc Afwijkingen');

        // Photo-based discrepancies
        if (payload.techDocAfwijkingen?.fotoAfwijkingen?.length > 0) {
            payload.techDocAfwijkingen.fotoAfwijkingen.forEach(item => {
                checkPageBreak(12);
                doc.setFontSize(9);
                doc.setFont('helvetica', 'bold');
                doc.setTextColor(200, 150, 0); // Yellow-ish for warning
                doc.text(`${item.type.toUpperCase()}${item.tagName ? ' - ' + item.tagName : ''}:`, leftMargin, y);
                y += 4;
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(0, 0, 0);
                const noteLines = doc.splitTextToSize(item.note, contentWidth - 10);
                doc.text(noteLines, leftMargin + 5, y);
                y += noteLines.length * 4 + 3;
            });
        }

        // Tag/Label issues
        if (payload.techDocAfwijkingen?.tagLabel?.gemeld) {
            addField('Tag/Label Issue', payload.techDocAfwijkingen.tagLabel.details || 'Gemeld');
        }

        // Tekenpakket issues
        if (payload.techDocAfwijkingen?.tekenpakket?.gemeld) {
            addField('Tekenpakket Issue', payload.techDocAfwijkingen.tekenpakket.details || 'Gemeld');
        }

        y += 5;
    }

    // === FOTO'S ===
    const allPhotos = [
        ...(payload.fotos.loto || []),
        ...(payload.fotos.taak || []),
        ...(payload.fotos.storing || [])
    ];

    if (allPhotos.length > 0) {
        addSectionHeader(`Foto's (${allPhotos.length})`);

        // Add photos in a grid (2 per row)
        let photoX = leftMargin;
        const photoWidth = 80;
        const photoHeight = 60;

        for (let i = 0; i < allPhotos.length; i++) {
            checkPageBreak(photoHeight + 10);

            try {
                // Fetch and embed image
                const response = await fetch(allPhotos[i]);
                const blob = await response.blob();
                const base64 = await blobToBase64(blob);

                doc.addImage(base64, 'JPEG', photoX, y, photoWidth, photoHeight);
            } catch (e) {
                // Placeholder if image fails
                doc.setFillColor(240, 240, 240);
                doc.rect(photoX, y, photoWidth, photoHeight, 'F');
                doc.setFontSize(8);
                doc.setTextColor(150, 150, 150);
                doc.text('Foto niet beschikbaar', photoX + 5, y + 30);
                doc.setTextColor(0, 0, 0);
            }

            // Move to next column or row
            if (i % 2 === 0) {
                photoX = leftMargin + photoWidth + 10;
            } else {
                photoX = leftMargin;
                y += photoHeight + 5;
            }
        }

        // Adjust y for last row
        if (allPhotos.length % 2 === 1) {
            y += photoHeight + 5;
        }
    }

    // === FOOTER ===
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
            `RaconWorks Werkrapport - Gegenereerd ${new Date().toLocaleString('nl-NL')} - Pagina ${i}/${pageCount}`,
            pageWidth / 2,
            doc.internal.pageSize.getHeight() - 10,
            { align: 'center' }
        );
    }

    return doc;
};

/**
 * Download PDF with proper filename
 */
export const downloadPDF = (doc, payload) => {
    const filename = `Werkrapport_${payload.werkplanNr}_${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
    return filename;
};

/**
 * Generate and download CMMS-compatible JSON
 */
export const exportCMMS = (payload, config = { mode: 'download' }) => {
    if (config.mode === 'download') {
        const jsonString = JSON.stringify(payload, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `CMMS_Export_${payload.werkplanNr}_${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        return link.download;
    }

    // Future: API mode
    if (config.mode === 'api') {
        // Implement when customer API is available
        throw new Error('API export not yet implemented');
    }
};

/**
 * Helper: Convert Blob to Base64
 */
const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

/**
 * Main export handler with audit logging
 */
export const handleExport = async (type, permit, checklist, completionData, userId) => {
    const payload = buildExportPayload(permit, checklist, completionData);

    try {
        if (type === 'pdf') {
            const doc = await generatePDF(payload);
            const filename = downloadPDF(doc, payload);

            // Audit log
            auditService.logAction(userId, 'EXPORT_PDF', {
                permitId: permit.id,
                werkplanNr: payload.werkplanNr,
                filename
            }, permit.location || null);

            return { success: true, filename };
        }

        if (type === 'cmms') {
            const filename = exportCMMS(payload);

            // Audit log
            auditService.logAction(userId, 'EXPORT_CMMS', {
                permitId: permit.id,
                werkplanNr: payload.werkplanNr,
                filename,
                mode: 'download'
            }, permit.location || null);

            return { success: true, filename };
        }

        throw new Error('Unknown export type');
    } catch (error) {
        console.error('Export failed:', error);
        throw error;
    }
};
